import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import { Filter, Sort } from '@urc-ui/store/api/types';

interface LandingPageState {
  readonly managerAccounts: string[];
  readonly advertiserAccounts: string[];
  readonly campaignFilter: string[];
  readonly reportNameSearchTerm: string;
  readonly filters: Filter[];
  readonly visibleColumns: string[];
  readonly selectAll: boolean;
  readonly selectedRows: string[];
  readonly sortColumn: Sort | null;
  readonly pageSize: number;
}

const initialState: LandingPageState = {
  managerAccounts: [],
  advertiserAccounts: [],
  campaignFilter: [],
  reportNameSearchTerm: '',
  filters: [],
  visibleColumns: [],
  selectAll: false,
  selectedRows: [],
  sortColumn: null,
  pageSize: 50
};

export const flexibleReportsLandingPageSlice = createSlice({
  name: 'flexibleReportsLandingPageSlice',
  initialState,
  reducers: {
    setAccountFilter: (state, action: PayloadAction<{ managerAccounts: string[]; advertiserAccounts: string[] }>) => {
      state.managerAccounts = action.payload.managerAccounts;
      state.advertiserAccounts = action.payload.advertiserAccounts;
    },
    setCampaignFilter: (state, action: PayloadAction<string[]>) => {
      state.campaignFilter = action.payload;
    },
    setReportNameSearchTerm: (state, action: PayloadAction<string>) => {
      state.reportNameSearchTerm = action.payload;
    },
    setFilters: (state, action: PayloadAction<Filter[]>) => {
      state.filters = action.payload;
    },
    setVisibleColumns: (state, action: PayloadAction<string[]>) => {
      state.visibleColumns = action.payload;
    },
    setSelectAll: (state, action: PayloadAction<boolean>) => {
      if (!action.payload) {
        state.selectedRows = [];
      }
      state.selectAll = action.payload;
    },
    setSelectedRows: (state, action: PayloadAction<string[]>) => {
      state.selectedRows = action.payload;
    },
    setSortColumn: (state, action: PayloadAction<Sort | null>) => {
      state.sortColumn = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    }
  }
});

export const {
  setAccountFilter,
  setCampaignFilter,
  setReportNameSearchTerm,
  setFilters,
  setVisibleColumns,
  setSelectAll,
  setSelectedRows,
  setSortColumn,
  setPageSize
} = flexibleReportsLandingPageSlice.actions;

export const selectAccountsFilter = createSelector(
  (state: RootState) => state.flexibleReportsLandingPage.managerAccounts,
  (state: RootState) => state.flexibleReportsLandingPage.advertiserAccounts,
  (managerAccounts, advertiserAccounts) => ({
    managerAccounts,
    advertiserAccounts
  })
);
export const selectCampaignFilter = (state: RootState) => state.flexibleReportsLandingPage.campaignFilter;
export const selectReportNameSearchTerm = (state: RootState) => state.flexibleReportsLandingPage.reportNameSearchTerm;
export const selectFilters = (state: RootState) => state.flexibleReportsLandingPage.filters;
export const selectVisibleColumns = (state: RootState) => state.flexibleReportsLandingPage.visibleColumns;
export const selectSelectAll = (state: RootState) => state.flexibleReportsLandingPage.selectAll;
export const selectSelectedRows = (state: RootState) => state.flexibleReportsLandingPage.selectedRows;
export const selectSortColumn = (state: RootState) => state.flexibleReportsLandingPage.sortColumn;
export const selectPageSize = (state: RootState) => state.flexibleReportsLandingPage.pageSize;

export default flexibleReportsLandingPageSlice.reducer;
